import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, TrendingUp, Crown, ArrowLeft, Calendar } from 'lucide-react';
import api from '../services/api';

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        api.get('/admin/stats'),
api.get('/admin/users')
      ]);
      setStats(statsResponse.data);
      setUsers(usersResponse.data.users);
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 403) {
        alert('Access denied. Admin only.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: '#2e7d32' }}>Loading admin panel...</div>
      </div>
    );
  }

  const planColors = {
    free: '#9e9e9e',
    teacher: '#66bb6a',
    school: '#42a5f5'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'white',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            color: '#424242',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'linear-gradient(135deg, #ffd54f 0%, #ff9800 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Crown size={24} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#f57c00', margin: 0 }}>
            Admin Dashboard
          </h1>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Total Users */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#e3f2fd',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={20} color="#42a5f5" />
              </div>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Total Users</p>
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#424242', margin: 0 }}>
              {stats?.totalUsers || 0}
            </h3>
          </div>

          {/* Monthly Revenue */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#e8f5e9',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <DollarSign size={20} color="#66bb6a" />
              </div>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Monthly Revenue</p>
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#424242', margin: 0 }}>
              ${stats?.monthlyRevenue || 0}
            </h3>
          </div>

          {/* Total Generations */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#fff9c4',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUp size={20} color="#fbc02d" />
              </div>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Total Generations</p>
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#424242', margin: 0 }}>
              {stats?.totalGenerations || 0}
            </h3>
          </div>

          {/* Paid Subscribers */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#fce4ec',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Crown size={20} color="#ec407a" />
              </div>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Paid Subscribers</p>
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#424242', margin: 0 }}>
              {stats?.paidSubscribers || 0}
            </h3>
          </div>
        </div>

        {/* Users Table */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#2e7d32', marginBottom: '24px' }}>
            All Users
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                    User
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                    Plan
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                    Generations This Month
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '16px' }}>
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#424242', margin: 0 }}>
                          {user.first_name} {user.last_name}
                        </p>
                        <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>
                          {user.email}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: `${planColors[user.plan_type]}20`,
                        color: planColors[user.plan_type],
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {user.plan_type}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: user.status === 'active' ? '#e8f5e9' : '#ffebee',
                        color: user.status === 'active' ? '#2e7d32' : '#c62828',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {user.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#424242' }}>
                      {user.generations_count || 0}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}